/**
 * Caja: abrir la sesión con el fondo fijo de la caja, ver la actual (turno, límite de efectivo), registrar retiros
 * parciales y cerrarla contando a ciegas. El cajero no ve aquí lo recaudado (spec 11, enmienda 2026-10-09).
 */
import { abrirCajaInput, Money, type AbrirCajaInput, type CajaDTO, type SesionCajaDTO, type SucursalDTO } from '@mg/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banknote, Lock, Wallet } from 'lucide-react';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import { Dinero } from '@/componentes/Dinero.tsx';
import { EstadoQuery, RequiereSucursal } from '@/componentes/estados.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/componentes/ui/card.tsx';
import { Input, Select } from '@/componentes/ui/input.tsx';
import { Campo } from '@/componentes/ui/label.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/componentes/ui/table.tsx';
import { TituloPagina } from '@/layout/AppLayout.tsx';
import { AvisoCajaOtraSucursal } from '@/modulos/caja/AbrirCaja.tsx';
import { AvisoRetiro, DialogoRetiro, RetirosCard } from '@/modulos/caja/Retiros.tsx';
import { CierreDialog } from '@/modulos/cierres/CierreDialog.tsx';
import { get, mensajeDeError, post } from '@/lib/api.ts';
import { useAuth } from '@/lib/auth.tsx';
import { claves } from '@/lib/query.ts';
import { formatearFechaHora } from '@/lib/utils.ts';
import { normalizarDecimal } from '@/lib/montos.ts';

type SucursalMia = SucursalDTO & { cajas: CajaDTO[] };
type Pagina = { datos: SesionCajaDTO[]; total: number };
const SUBTITULO = 'Sesión de caja del cajero: apertura con el fondo de la caja, retiros parciales y cierre contando a ciegas.';

export function Caja() {
  const { usuario, recargar, puede } = useAuth();
  const qc = useQueryClient();
  /** Sesión que se está cerrando: el cierre SIEMPRE pasa por el conteo por método (observación del cliente 24-09-2026). */
  const [cerrando, setCerrando] = useState<string | null>(null);
  const [retirando, setRetirando] = useState(false);
  // Retiro sobre la caja abierta de otro cajero (la dueña o quien autoriza cierres; nace confirmado: hallazgo E3)
  const [retiroAjeno, setRetiroAjeno] = useState<{ id: string; caja_nombre: string } | null>(null);

  // Superadmin en consola global (sin contexto): la caja es de una sucursal, no se consulta nada (ADR-0003)
  const enSucursal = Boolean(usuario?.sucursal_id);
  const mia = useQuery({ queryKey: claves.sucursalMia, queryFn: () => get<SucursalMia | null>('/sucursales/mia'), enabled: enSucursal });
  const actual = useQuery({ queryKey: claves.sesionCajaActual, queryFn: () => get<SesionCajaDTO | null>('/caja/sesiones/actual'), enabled: enSucursal, refetchInterval: 60_000 });
  const historial = useQuery({ queryKey: claves.sesionesCaja({ limite: 20 }), queryFn: () => get<Pagina>('/caja/sesiones', { limite: 20 }), enabled: enSucursal });

  const form = useForm<AbrirCajaInput>({ resolver: zodResolver(abrirCajaInput), defaultValues: { caja_id: '', fondo_usd: '0', fondo_bs: '0' } });
  const [cajaId, fondoUsd, fondoBs] = useWatch({ control: form.control, name: ['caja_id', 'fondo_usd', 'fondo_bs'] });
  const cajaElegida = (mia.data?.cajas ?? []).find((c) => c.id === cajaId);
  const igual = (a: string | undefined, b: string | undefined) => {
    try {
      return Money.parse(normalizarDecimal(a ?? '0') || '0').eq(Money.parse(b ?? '0'));
    } catch {
      return true;
    }
  };
  const fondoDistinto = !!cajaElegida && (!igual(fondoUsd, cajaElegida.fondo_usd) || !igual(fondoBs, cajaElegida.fondo_bs));

  const invalidar = async () => {
    await Promise.all([qc.invalidateQueries({ queryKey: ['caja'] }), qc.invalidateQueries({ queryKey: ['cierres'] }), recargar()]);
  };

  const abrir = useMutation({
    mutationFn: (datos: AbrirCajaInput) => post<SesionCajaDTO>('/caja/sesiones/abrir', datos),
    onSuccess: async (s) => {
      toast.success(`${s.caja_nombre} abierta · turno ${s.turno}`, { description: `Fondo $ ${Money.parse(s.fondo_usd).toDisplay()} · Bs ${Money.parse(s.fondo_bs).toDisplay()}${s.fondo_distinto ? ' · distinto al fondo fijo: se avisó a la dueña' : ''}` });
      form.reset();
      await invalidar();
    },
    onError: (e) => toast.error('No se pudo abrir la caja', { description: mensajeDeError(e) }),
  });

  const puedeAbrir = puede('caja.abrir', 'total');
  const confirmaRetiros = puede('caja.confirmar-retiro');

  if (!enSucursal) {
    return (
      <>
        <TituloPagina titulo="Caja" subtitulo={SUBTITULO} />
        {/* La dueña confirma los retiros desde su usuario, sin entrar a una tienda */}
        {confirmaRetiros ? <div className="mb-4"><RetirosCard soloPorConfirmar /></div> : null}
        <RequiereSucursal />
      </>
    );
  }

  return (
    <>
      <TituloPagina titulo="Caja" subtitulo={SUBTITULO} />
      {confirmaRetiros ? <div className="mb-4"><RetirosCard soloPorConfirmar /></div> : null}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Mi sesión</CardTitle>
            <CardDescription>{usuario?.sucursal_nombre}</CardDescription>
          </CardHeader>
          <CardContent>
            <EstadoQuery cargando={actual.isLoading} error={actual.error} datos={actual.data === undefined ? undefined : { s: actual.data }} reintentar={() => void actual.refetch()}>
              {({ s }) =>
                s ? (
                  <div className="space-y-3" data-testid="sesion-actual">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-lg font-semibold">{s.caja_nombre} <span className="text-sm font-normal text-muted-foreground" data-testid="sesion-turno">· turno {s.turno}</span></p>
                        <p className="text-xs text-muted-foreground">Abierta {formatearFechaHora(s.abierta_en)}{s.punto_banco ? ` · punto ${s.punto_banco}` : ''}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge variant={s.declarado ? 'pendiente' : 'ok'}>{s.declarado ? 'Conteo declarado' : 'Abierta'}</Badge>
                        {s.modalidad === 'solo_fiscal' ? <Badge variant="fiscal" data-testid="sesion-solo-fiscal">Solo facturas</Badge> : null}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 rounded-xl bg-muted/60 p-3 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground">Fondo $</p>
                        <Dinero valor={s.fondo_usd} modo="usd" alineado="izquierda" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Fondo Bs</p>
                        <Dinero valor={s.fondo_bs} moneda="BS" modo="bs" alineado="izquierda" />
                      </div>
                    </div>
                    {s.fondo_distinto ? <p className="text-xs text-mg-ambar" data-testid="sesion-fondo-distinto">Abriste con un fondo distinto al fijo de la caja: la dueña recibió el aviso.</p> : null}
                    <AvisoRetiro retiro={s.retiro} alRetirar={() => setRetirando(true)} />
                    {s.declarado ? <p className="text-sm text-mg-ambar">Ya declaraste tu conteo: la caja no vende hasta terminar el cierre.</p> : null}
                    <div className="flex flex-wrap gap-2">
                      {!s.declarado ? <Button variant="outline" onClick={() => setRetirando(true)} data-testid="btn-retiro"><Banknote /> Retiro parcial</Button> : null}
                      <Button variant="destructive" onClick={() => setCerrando(s.id)} data-testid="btn-cerrar-caja"><Lock /> {s.declarado ? 'Terminar cierre' : 'Contar y cerrar caja'}</Button>
                    </div>
                  </div>
                ) : usuario?.caja_en_otra_sucursal ? (
                  <AvisoCajaOtraSucursal />
                ) : puedeAbrir ? (
                  <form className="space-y-3" data-testid="form-abrir-caja" onSubmit={form.handleSubmit((d) => abrir.mutate(d))} noValidate>
                    <Campo etiqueta="Caja" htmlFor="caja_id" error={form.formState.errors.caja_id?.message}>
                      <Select
                        id="caja_id"
                        {...form.register('caja_id', {
                          // Al elegir la caja se propone su fondo fijo (el que quedó del turno anterior)
                          onChange: (e: { target: { value: string } }) => {
                            const c = (mia.data?.cajas ?? []).find((x) => x.id === e.target.value);
                            form.setValue('fondo_usd', c?.fondo_usd ?? '0');
                            form.setValue('fondo_bs', c?.fondo_bs ?? '0');
                          },
                        })}
                        aria-invalid={!!form.formState.errors.caja_id}
                      >
                        <option value="">Selecciona una caja…</option>
                        {(mia.data?.cajas ?? []).map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.nombre}
                          </option>
                        ))}
                      </Select>
                    </Campo>
                    <div className="grid grid-cols-2 gap-3">
                      <Campo etiqueta="Fondo inicial $" htmlFor="fondo_usd" error={form.formState.errors.fondo_usd?.message} ayuda="El fondo fijo de la caja">
                        <Input id="fondo_usd" inputMode="decimal" {...form.register('fondo_usd', { setValueAs: (v: string) => normalizarDecimal(v ?? '') })} aria-invalid={!!form.formState.errors.fondo_usd} />
                      </Campo>
                      <Campo etiqueta="Fondo inicial Bs" htmlFor="fondo_bs" error={form.formState.errors.fondo_bs?.message} ayuda="Confírmalo contando lo que hay">
                        <Input id="fondo_bs" inputMode="decimal" {...form.register('fondo_bs', { setValueAs: (v: string) => normalizarDecimal(v ?? '') })} aria-invalid={!!form.formState.errors.fondo_bs} />
                      </Campo>
                    </div>
                    {cajaElegida ? (
                      <p className={`text-xs ${fondoDistinto ? 'text-mg-ambar' : 'text-muted-foreground'}`} data-testid="fondo-fijo">
                        Fondo fijo de {cajaElegida.nombre}: $ {Money.parse(cajaElegida.fondo_usd ?? '0').toDisplay()} · Bs {Money.parse(cajaElegida.fondo_bs ?? '0').toDisplay()}.
                        {fondoDistinto ? ' Estás abriendo con otro monto: se avisará a la dueña.' : ' Si encuentras otra cosa, escribe lo que hay.'}
                        {cajaElegida.modalidad === 'solo_fiscal' ? ' Esta caja emite solo facturas.' : ''}
                      </p>
                    ) : null}
                    <Button type="submit" cargando={abrir.isPending} data-testid="btn-abrir-caja">
                      <Wallet /> Abrir caja
                    </Button>
                  </form>
                ) : (
                  <p className="text-sm text-muted-foreground">Tu rol solo consulta las sesiones de caja.</p>
                )
              }
            </EstadoQuery>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Sesiones de la sucursal</CardTitle>
            <CardDescription>Últimas 20</CardDescription>
          </CardHeader>
          <CardContent>
            <EstadoQuery cargando={historial.isLoading} error={historial.error} datos={historial.data} vacio={(d) => d.datos.length === 0} reintentar={() => void historial.refetch()}>
              {(d) => (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Caja</TableHead>
                      <TableHead>Cajero</TableHead>
                      <TableHead>Apertura</TableHead>
                      <TableHead className="text-right">Fondo</TableHead>
                      <TableHead>Estado</TableHead>
                      {puede('caja.cerrar-con-diferencia') ? <TableHead /> : null}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {d.datos.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell className="font-medium">{s.caja_nombre}<span className="block text-[11px] font-normal text-muted-foreground">turno {s.turno}</span></TableCell>
                        <TableCell>{s.usuario_nombre}</TableCell>
                        <TableCell>{formatearFechaHora(s.abierta_en)}</TableCell>
                        <TableCell className="text-right">
                          <Dinero valor={s.fondo_usd} modo="usd" />
                        </TableCell>
                        <TableCell>
                          <Badge variant={s.estado === 'abierta' ? (s.declarado ? 'pendiente' : 'ok') : 'neutro'}>{s.estado === 'abierta' ? (s.declarado ? 'Conteo declarado' : 'Abierta') : 'Cerrada'}</Badge>
                        </TableCell>
                        {puede('caja.cerrar-con-diferencia') ? (
                          <TableCell className="text-right">
                            {s.estado === 'abierta' && !s.declarado && s.id !== actual.data?.id ? <Button size="sm" variant="outline" onClick={() => setRetiroAjeno({ id: s.id, caja_nombre: s.caja_nombre })} data-testid={`caja-retiro-ajeno-${s.id}`}>Retiro</Button> : null}
                          </TableCell>
                        ) : null}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </EstadoQuery>
          </CardContent>
        </Card>
      </div>
      <div className="mt-4">
        <RetirosCard />
      </div>

      {actual.data ? <DialogoRetiro abierto={retirando} cerrar={() => setRetirando(false)} cajaNombre={actual.data.caja_nombre} /> : null}
      {retiroAjeno ? <DialogoRetiro abierto cerrar={() => setRetiroAjeno(null)} cajaNombre={retiroAjeno.caja_nombre} sesionId={retiroAjeno.id} /> : null}
      <CierreDialog sesionId={cerrando} cerrar={() => setCerrando(null)} alCerrar={() => void invalidar()} />
    </>
  );
}
